export type TradingViewLabelStyle = 'label_up' | 'label_down'

export type TradingViewPatternLabelOptions = {
    fontSize?: number
    bold?: boolean
    horizontalPixelRatio?: number
    verticalPixelRatio?: number
}

/** TradingView-style rounded label (no pointer) — positioned above/below anchor */
export function drawTradingViewPatternLabel(
    ctx: CanvasRenderingContext2D,
    centerX: number,
    anchorY: number,
    text: string,
    backgroundColor: string,
    style: TradingViewLabelStyle,
    options: TradingViewPatternLabelOptions = {},
) {
    const hpr = options.horizontalPixelRatio ?? 1
    const vpr = options.verticalPixelRatio ?? 1
    const fontSize = (options.fontSize ?? 11) * vpr
    const fontWeight = options.bold ? 'bold' : 'normal'
    const padX = 6 * hpr
    const padY = 4 * vpr
    const radius = 2 * hpr
    const gap = 4 * vpr

    ctx.save()
    ctx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const textWidth = ctx.measureText(text).width
    const textMetrics = ctx.measureText(text)
    const textH = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent || fontSize
    const boxW = textWidth + padX * 2
    const boxH = textH + padY * 2
    const boxLeft = centerX - boxW / 2
    const boxTop = style === 'label_down'
        ? anchorY - gap - boxH
        : anchorY + gap

    ctx.fillStyle = backgroundColor
    ctx.beginPath()
    ctx.roundRect(boxLeft, boxTop, boxW, boxH, radius)
    ctx.fill()

    ctx.fillStyle = '#FFFFFF'
    ctx.fillText(text, centerX, boxTop + boxH / 2)
    ctx.restore()
}

export function measureTradingViewPatternLabelBounds(
    centerX: number,
    anchorY: number,
    text: string,
    style: TradingViewLabelStyle,
    options: TradingViewPatternLabelOptions = {},
    measurer?: Pick<CanvasRenderingContext2D, 'font' | 'measureText'>,
): { left: number; right: number; top: number; bottom: number } {
    const hpr = options.horizontalPixelRatio ?? 1
    const vpr = options.verticalPixelRatio ?? 1
    const fontSize = (options.fontSize ?? 11) * vpr
    const fontWeight = options.bold ? 'bold' : 'normal'
    const padX = 6 * hpr
    const padY = 4 * vpr
    const gap = 4 * vpr

    let textWidth = text.length * fontSize * 0.6
    let textH = fontSize
    if (measurer) {
        measurer.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, sans-serif`
        const metrics = measurer.measureText(text)
        textWidth = metrics.width
        textH = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent || fontSize
    }

    const boxW = textWidth + padX * 2
    const boxH = textH + padY * 2
    const boxLeft = centerX - boxW / 2
    const boxTop = style === 'label_down'
        ? anchorY - gap - boxH
        : anchorY + gap

    return {
        left: boxLeft,
        right: boxLeft + boxW,
        top: boxTop,
        bottom: boxTop + boxH,
    }
}
